import { API_ENDPOINTS } from '@api/ApiEndpoints';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { PersonsResponse, UsePersonsQueryOptions } from '../people/types';
import {
  CreateLocationData,
  CreateLocationResponse,
  DeleteLocationResponse,
  Location,
  LocationForm,
  UpdateLocationMembersData,
  UpdateLocationMembersResponse,
  UseCreateLocationOptions,
  UseUpdateLocationMembersOptions,
  UseUpdateLocationOptions,
} from './types';
import { useHttpClient } from '@hooks/utils/httpConfig';
import { getSearchQueryParams } from '@utils/index';

export function useLocationsQuery(): UseQueryResult<Location[]> {
  const locationsClient = useHttpClient();
  const fetchLocations = async (): Promise<Location[]> => {
    return locationsClient.get<Location[]>(API_ENDPOINTS.LOCATIONS);
  };

  return useQuery({ queryKey: ['locations'], queryFn: fetchLocations });
}

export function useMembersQuery({
  searchTerm,
  sortBy,
  order,
  personStatus,
  pageSize = 20,
  pageNumber = 1,
  locationId,
}: UsePersonsQueryOptions = {}): UseQueryResult<PersonsResponse> {
  const locationsClient = useHttpClient();
  const fetchMembers = async (): Promise<PersonsResponse> => {
    const params = getSearchQueryParams({
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      status: personStatus,
      locationId,
    });
    const endpoint = `${API_ENDPOINTS.LOCATIONS}/${locationId}/members?${params.toString()}`;
    return locationsClient.get<PersonsResponse>(endpoint);
  };

  return useQuery({ queryKey: [
      'locationMembers',
      pageSize,
      pageNumber,
      searchTerm,
      sortBy,
      order,
      personStatus,
    ], queryFn: fetchMembers });
}

export const useUpdateLocationMembers = (
  locationId: string | undefined,
  options?: UseUpdateLocationMembersOptions
): UseMutationResult<
  UpdateLocationMembersResponse,
  unknown,
  UpdateLocationMembersData
> => {
  const locationsClient = useHttpClient();
  return useMutation({
    mutationFn: (data: UpdateLocationMembersData) =>
      locationsClient.patch<UpdateLocationMembersResponse>(
        `${API_ENDPOINTS.LOCATIONS}/${locationId}/members`,
        data
      ),
    ...options,
  });
};

export const useDeleteLocation = (): UseMutationResult<
  DeleteLocationResponse,
  unknown,
  string,
  unknown
> => {
  const locationsClient = useHttpClient();
  return useMutation({ mutationFn: (locationId: string) =>
    locationsClient.delete<DeleteLocationResponse>(
      `${API_ENDPOINTS.LOCATIONS}/${locationId}`
    ) });
};

export const useUpdateLocation = (
  locationId: string | undefined,
  options?: UseUpdateLocationOptions
): UseMutationResult<unknown, unknown, LocationForm> => {
  const locationsClient = useHttpClient();
  return useMutation({
    mutationFn: (data: LocationForm) =>
      locationsClient.patch(`${API_ENDPOINTS.LOCATIONS}/${locationId}`, data),
    ...options,
  });
};

export const useCreateLocation = (
  options?: UseCreateLocationOptions
): UseMutationResult<CreateLocationResponse, unknown, CreateLocationData> => {
  const locationsClient = useHttpClient();
  return useMutation({
    mutationFn: (data: CreateLocationData) =>
      locationsClient.post<CreateLocationResponse>(
        `${API_ENDPOINTS.LOCATIONS}`,
        data
      ),
    ...options,
  });
};
